import React, { createContext, useState, useEffect, useContext, ReactNode } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme, Theme, responsiveFontSizes } from '@mui/material/styles';
import { FilterCSS } from 'cssfilter';
import { TenantBranding, Tenant } from '../services/tenantService';
import { useTenantBranding, useTenantInfo } from '../hooks/useTenantData';
import defaultTheme from '../theme';

/**
 * CSS sanitizer using cssfilter library.
 * Removes dangerous CSS patterns (url, expression, javascript:, etc.)
 * while preserving safe styling properties.
 */
const cssFilter = new FilterCSS({
  // Allowlist of safe CSS properties (cssfilter's default is comprehensive)
  // We add onAttr to explicitly block url() and other dangerous values
  onAttr: (name: string, value: string) => {
    // Block url() values - can exfiltrate data
    if (/url\s*\(/i.test(value)) {
      return '';
    }
    // Block expression() - IE JavaScript execution
    if (/expression\s*\(/i.test(value)) {
      return '';
    }
    // Block javascript: pseudo-protocol
    if (/javascript\s*:/i.test(value)) {
      return '';
    }
    // Block behavior - IE HTC files
    if (name.toLowerCase() === 'behavior') {
      return '';
    }
    // Block -moz-binding - Firefox XBL
    if (name.toLowerCase() === '-moz-binding') {
      return '';
    }
    // Allow the property
    return;
  }
});

const sanitizeCss = (css: string): string => {
  if (!css) return '';
  // Also strip @import and </style> tags before processing
  const cleaned = css
    .replace(/@import\s+[^;]+;?/gi, '')
    .replace(/<\s*\/?\s*style[^>]*>/gi, '');
  return cssFilter.process(cleaned);
};

// Helper function to lighten a color
const lightenColor = (color: string, amount: number): string => {
  try {
    // Convert hex to RGB
    let r = parseInt(color.substring(1, 3), 16);
    let g = parseInt(color.substring(3, 5), 16);
    let b = parseInt(color.substring(5, 7), 16);

    // Lighten
    r = Math.min(255, Math.round(r + (255 - r) * amount));
    g = Math.min(255, Math.round(g + (255 - g) * amount));
    b = Math.min(255, Math.round(b + (255 - b) * amount));

    // Convert back to hex
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
  } catch (error) {
    console.error('Error lightening color:', error);
    return color;
  }
};

// Helper function to darken a color
const darkenColor = (color: string, amount: number): string => {
  try {
    // Convert hex to RGB
    let r = parseInt(color.substring(1, 3), 16);
    let g = parseInt(color.substring(3, 5), 16);
    let b = parseInt(color.substring(5, 7), 16);

    // Darken
    r = Math.max(0, Math.round(r * (1 - amount)));
    g = Math.max(0, Math.round(g * (1 - amount)));
    b = Math.max(0, Math.round(b * (1 - amount)));

    // Convert back to hex
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
  } catch (error) {
    console.error('Error darkening color:', error);
    return color;
  }
};

interface ThemeContextType {
  branding: TenantBranding | null;
  tenantInfo: Tenant | null;
  theme: Theme;
  loading: boolean;
}

const ThemeContext = createContext<ThemeContextType>({
  branding: null,
  tenantInfo: null,
  theme: defaultTheme,
  loading: true
});

interface ThemeProviderProps {
  children: ReactNode;
}

export const CustomThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [theme, setTheme] = useState<Theme>(defaultTheme);

  // Use React Query hooks for automatic caching and deduplication
  const { data: brandingData, isLoading: brandingLoading } = useTenantBranding();
  const { data: tenantInfoData, isLoading: tenantInfoLoading } = useTenantInfo();

  // Convert undefined to null for compatibility with existing context type
  const branding = brandingData ?? null;
  const tenantInfo = tenantInfoData ?? null;
  const loading = brandingLoading || tenantInfoLoading;

  const createThemeFromBranding = (brandingData: TenantBranding): Theme => {
    const customTheme = createTheme({
      palette: {
        primary: {
          main: brandingData.primaryColor,
          // Generate light and dark variations automatically
          light: lightenColor(brandingData.primaryColor, 0.25),
          dark: darkenColor(brandingData.primaryColor, 0.25),
          contrastText: '#FFFFFF',
        },
        secondary: {
          main: brandingData.secondaryColor,
          light: lightenColor(brandingData.secondaryColor, 0.25),
          dark: darkenColor(brandingData.secondaryColor, 0.25),
          contrastText: '#FFFFFF',
        },
        tertiary: {
          main: brandingData.tertiaryColor,
          light: lightenColor(brandingData.tertiaryColor, 0.25),
          dark: darkenColor(brandingData.tertiaryColor, 0.25),
          contrastText: '#000000',
        },
        accent: {
          main: brandingData.accentColor,
          light: lightenColor(brandingData.accentColor, 0.25),
          dark: darkenColor(brandingData.accentColor, 0.25),
          contrastText: '#FFFFFF',
        },
        background: {
          default: '#F5F7FA',
          paper: '#FFFFFF',
        },
        text: {
          primary: '#263238',
          secondary: '#546E7A',
        },
      },
      typography: {
        fontFamily: brandingData.fontFamily,
        h1: {
          fontWeight: 700,
        },
        h2: {
          fontWeight: 600,
        },
        h3: {
          fontWeight: 600,
        },
        h4: {
          fontWeight: 600,
        },
        h5: {
          fontWeight: 500,
        },
        h6: {
          fontWeight: 500,
        },
        subtitle1: {
          fontWeight: 400,
        },
        subtitle2: {
          fontWeight: 400,
        },
        body1: {
          fontWeight: 400,
        },
        body2: {
          fontWeight: 400,
        },
        button: {
          fontWeight: 500,
          textTransform: 'none',
        },
      },
      shape: {
        borderRadius: 8,
      },
      components: {
        MuiButton: {
          styleOverrides: {
            root: {
              boxShadow: 'none',
              '&:hover': {
                boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
              },
            },
            contained: {
              padding: '10px 24px',
            },
          },
        },
        MuiPaper: {
          styleOverrides: {
            root: {
              boxShadow: '0px 3px 15px rgba(0, 0, 0, 0.05)',
            },
          },
        },
        MuiCard: {
          styleOverrides: {
            root: {
              overflow: 'hidden',
              boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
            },
          },
        },
        MuiTextField: {
          styleOverrides: {
            root: {
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: brandingData.primaryColor,
                },
              },
            },
          },
        },
      },
    });
    
    // Make typography responsive
    return responsiveFontSizes(customTheme);
  };

  // Update theme whenever branding data changes
  useEffect(() => {
    if (branding) {
      const customTheme = createThemeFromBranding(branding);
      setTheme(customTheme);
    } else if (!brandingLoading) {
      // If no branding and not loading, use default theme
      setTheme(defaultTheme);
    }
  }, [branding, brandingLoading]);

  // Apply custom CSS if provided (sanitized to prevent XSS)
  useEffect(() => {
    if (branding?.customCss) {
      // Sanitize CSS before injection to prevent XSS attacks
      const sanitizedCss = sanitizeCss(branding.customCss);

      // Create a style element
      const styleElement = document.createElement('style');
      styleElement.id = 'tenant-custom-css';
      styleElement.textContent = sanitizedCss; // Use textContent instead of innerHTML for safety
      document.head.appendChild(styleElement);

      return () => {
        // Cleanup on unmount
        const existingStyle = document.getElementById('tenant-custom-css');
        if (existingStyle) {
          document.head.removeChild(existingStyle);
        }
      };
    }
  }, [branding]);

  // Set favicon if provided
  useEffect(() => {
    if (branding?.faviconUrl) {
      const faviconLink = document.querySelector("link[rel*='icon']") as HTMLLinkElement;
      if (faviconLink) {
        faviconLink.href = branding.faviconUrl;
      } else {
        const newFaviconLink = document.createElement('link');
        newFaviconLink.rel = 'shortcut icon';
        newFaviconLink.href = branding.faviconUrl;
        document.head.appendChild(newFaviconLink);
      }
    }
  }, [branding]);

  return (
    <ThemeContext.Provider value={{ branding, tenantInfo, theme, loading }}>
      <MuiThemeProvider theme={theme}>
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export const useThemeContext = () => useContext(ThemeContext);

export default ThemeContext;